/* ============================================================================
   ffg-buybar-total.js — the mobile buy bar shows the LINE TOTAL, not the unit
   price. 2 x Large reads $18.00, not $9.00.

   READ THIS BEFORE CHANGING ANYTHING. The constraint that shapes the whole
   file is in claude/horizoncascadefieldnotes.md §2 and in the header of
   blocks/custom-product-copy.liquid: HORIZON RE-RENDERS THE ENTIRE PRODUCT
   FORM through the Section Rendering API on every variant change. Anything
   this file injects into that subtree is wiped on the first tap.

   So it injects nothing. The bar's price is still drawn by CSS `content`, the
   same as before. All this file does is compute a STRING and park it on
   <html>, which Section Rendering never touches:

       --ffg-bar-total : "$18.00"      (inherits down into the ::after)
       data-ffg-qty-total              (the switch that makes the rule apply)

   THE RULE ONLY EXISTS WHILE QUANTITY > 1. At quantity 1 the total IS the unit
   price, the generated per-variant rule already says so, and this file removes
   both the property and the attribute. That is deliberate: if this script
   fails to load, throws, or is blocked, the bar falls back to exactly the
   behaviour it had before — correct at quantity 1, stale above it. There is no
   state in which a broken script shows a wrong number rather than an old one.

   It writes two strings, on the same switch:
     --ffg-bar-total : the line total          ("$18.00")
     --ffg-bar-name  : the item name, pluralised ("LARGE BARS")

   And two numbers, on every sync:
     --ffg-bar-name-size : the font size at which the name is exactly as wide
                           as the price beneath it, so the two stack flush.
     --ffg-bar-price-fit : the price's size, reduced ONLY when the full-size
                           price would not fit the column at all.

   Depends on three things emitted by blocks/custom-product-copy.liquid:
     --ffg-unit-cents  on .product-form-buttons, set per checked variant
     --ffg-unit-name   the singular label for that variant, same string the
                       generated ::before rule uses
     the chip's ::after content, which is Shopify's own money formatting and is
     used here as the FORMAT SAMPLE — see formatMoney.
   ========================================================================= */
(function () {
  'use strict';

  /* The block emits this tag inside the section, so a re-render re-inserts it
     and the browser runs it again. Idempotent by flag rather than by moving
     the tag somewhere "safe" — the block is where the guard condition lives. */
  if (window.__ffgBuyBarTotal) return;
  window.__ffgBuyBarTotal = true;

  var ROOT = document.documentElement;
  var VAR = '--ffg-bar-total';
  var VAR_NAME = '--ffg-bar-name';
  var VAR_SIZE = '--ffg-bar-name-size';
  var VAR_PRICE = '--ffg-bar-price-fit';
  var ATTR = 'data-ffg-qty-total';
  var BAR = '.product-details .product-form-buttons';
  var QTY = '.product-details .quantity-selector input[type="number"]';
  var frame = 0;

  function readCents(el) {
    var n = parseInt(getComputedStyle(el).getPropertyValue('--ffg-unit-cents'), 10);
    return isFinite(n) && n >= 0 ? n : null;
  }

  function readProp(el, name) {
    var v = getComputedStyle(el).getPropertyValue(name).trim();
    if (!v) return null;
    return v.replace(/^["']/, '').replace(/["']$/, '').replace(/\\"/g, '"');
  }

  /* ONE BAR, TWO BARS. Only the last word changes, so "6 Pack Box" becomes
     "6 Pack Boxes" and "3-Pack" becomes "3-Packs".

     The three rules below cover this catalogue and ordinary English; anything
     irregular is not guessed at — Liquid can emit --ffg-unit-name-plural and
     this function is skipped entirely. That escape hatch exists so nobody has
     to edit JavaScript to fix a word.

     The suffix takes the case of the letter it follows, because the label may
     arrive already shouted ("3-PACK" -> "3-PACKS") or in title case
     ("Large Bar" -> "Large Bars"). The bar's own text-transform would hide a
     mistake here on the phone, but not everywhere this string could be used. */
  function pluralise(name) {
    /* IF IT DOESN'T END IN A LETTER, LEAVE IT. Measured against the real
       catalogue on 29 Jul: option values include "Large (Man Pirate)",
       "Man Pack (8)", "Wacky Pack (12)" and the gift-certificate values "$25".
       Appending to any of those produces "Large (Man Pirate)s". Declining to
       pluralise reads as slightly stiff; pluralising reads as broken. */
    if (!/[A-Za-z]$/.test(name)) return name;

    return name.replace(/(\S+)\s*$/, function (word) {
      var last = word.charAt(word.length - 1);
      var shout = last === last.toUpperCase() && last !== last.toLowerCase();
      var lower = word.toLowerCase();

      /* ALREADY PLURAL — leave it alone. Some option values are written plural
         ("Mixed Cases", "12 Bars"), and without this they come out "Caseses".
         The exclusions are the singulars that genuinely end in s: bus, iris,
         dress. Everything else ending in a lone s is treated as a plural. */
      if (/[^s]s$/.test(lower) && !/(us|is|ss)$/.test(lower)) return word;

      var suffix;
      if (/(s|x|z|ch|sh)$/.test(lower)) {
        suffix = 'es';
      } else if (/[^aeiou]y$/.test(lower)) {
        word = word.slice(0, -1);
        suffix = 'ies';
      } else {
        suffix = 's';
      }
      return word + (shout ? suffix.toUpperCase() : suffix);
    });
  }

  /* ---- Measuring ---------------------------------------------------------
     One hidden span, appended to <body> — deliberately OUTSIDE the product
     section, so Section Rendering never removes it and this stays a
     create-once. Canvas measureText was the first draft and was dropped: its
     letterSpacing support is uneven, and it cannot apply text-transform, which
     this label leans on entirely (the source string is "Large Bar"; every
     pixel on screen is uppercase).

     letter-spacing arrives from getComputedStyle already RESOLVED TO PX at the
     element's current size. Measuring at a reference size therefore has to
     re-scale it, or a 0.02em tracking measured at 100px comes out 6x too tight
     and every name is sized too large. */
  var ruler = null;
  function measureText(text, cs, sizePx) {
    if (!ruler) {
      ruler = document.createElement('span');
      ruler.setAttribute('aria-hidden', 'true');
      ruler.style.cssText =
        'position:absolute;left:-9999px;top:-9999px;white-space:pre;' +
        'visibility:hidden;pointer-events:none;';
      document.body.appendChild(ruler);
    }
    var current = parseFloat(cs.fontSize) || 16;
    var tracking = parseFloat(cs.letterSpacing);
    ruler.style.fontFamily = cs.fontFamily;
    ruler.style.fontWeight = cs.fontWeight;
    ruler.style.fontStyle = cs.fontStyle;
    ruler.style.fontStretch = cs.fontStretch;
    ruler.style.textTransform = cs.textTransform;
    ruler.style.letterSpacing = isFinite(tracking)
      ? (tracking / current * sizePx) + 'px'
      : 'normal';
    ruler.style.fontSize = sizePx + 'px';
    ruler.textContent = text;
    return ruler.getBoundingClientRect().width;
  }

  function pseudoText(cs) {
    var c = cs.content;
    if (!c || c === 'none' || c === 'normal') return null;
    return c.replace(/^["']/, '').replace(/["']$/, '').replace(/\\"/g, '"');
  }

  /* ---- The name is sized to the price ------------------------------------
     Devin, 29 Jul: the name and the price sit one above the other in layout B,
     and their right edges wandered — "3-PACK" fell well short of "$21.00",
     "SMALL BAR" overhung "$3.50". Both are the same two strings whose lengths
     move independently: the price grows with quantity, the name grows with the
     variant and again with the plural. No fixed pair of font sizes can hold
     that, so the name's size is solved for on every change.

     ALL THE TYPE METRICS THAT MATTER ARE em-BASED — the tracking scales with
     the size — so width is linear in font-size and one measurement at a
     reference size gives the answer outright. No iteration, no binary search.

     Bounded at both ends, and the ceiling defaults to the PRICE's own size so
     a short name can never out-shout the number it labels. Both bounds are
     read from CSS so they can be retuned, and the whole routine is opt-in via
     --ffg-bar-fit-name: 1 — which lives inside the mobile media query, so
     desktop and layout A are untouched without this file knowing they exist. */
  function fitName(bar) {
    var cs = getComputedStyle(bar);
    if (cs.getPropertyValue('--ffg-bar-fit-name').trim() !== '1') { unfit(); return; }

    var pre = getComputedStyle(bar, '::before');
    var post = getComputedStyle(bar, '::after');
    var name = pseudoText(pre);
    var price = pseudoText(post);
    if (!name || !price) { unfit(); return; }

    var REF = 100;
    var priceRef = measureText(price, post, REF);
    var nameRef = measureText(name, pre, REF);
    if (!priceRef || !nameRef) { unfit(); return; }

    /* THE CONFIGURED SIZE IS READ FROM ITS OWN PROPERTY, NOT FROM fontSize.
       fontSize is whatever this function set last time, so using it as the
       starting point would let each pass compound the previous one and the
       price would creep smaller on every sync. --ffg-bar-price-size is written
       only by the stylesheet, so it is a fixed point. */
    var cfg = parseFloat(cs.getPropertyValue('--ffg-bar-price-size'))
           || parseFloat(post.fontSize) || 31;

    /* The room the stacked pair actually has, READ OFF THE GRID rather than
       off the pseudo-element. Once the pair is centred with justify-self the
       ::after box shrink-wraps its own text, so its width is the price's width
       and using it here would mean "the price always exactly fits" — the
       shrink branch below could never fire.

       gridTemplateColumns computes to the USED track sizes, which is the
       number wanted. It is stable for the same reason as before: the middle
       track is minmax(0, 1fr) and the button's is auto, so the track is sized
       by what is left over, not by what is in it. Which track is the pair's is
       CSS's business, not this file's — hence the property, defaulting to the
       middle of three. */
    var idx = parseInt(cs.getPropertyValue('--ffg-bar-fit-track'), 10);
    if (!isFinite(idx)) idx = 2;
    var tracks = cs.gridTemplateColumns.split(' ').map(parseFloat);
    var room = tracks[idx - 1];
    if (isFinite(room)) {
      room -= (parseFloat(post.paddingLeft) || 0) + (parseFloat(post.paddingRight) || 0);
    }

    /* The price only gives way when it genuinely will not fit. 12 x 3-Pack is
       "$252.00", which at 31px is 131px of type in a 90px column on a 390px
       phone; before this it simply painted over the gap and reached the
       button. */
    var priceSize = cfg;
    if (isFinite(room) && room > 0 && priceRef * cfg / REF > room) {
      priceSize = REF * room / priceRef;
    }
    ROOT.style.setProperty(VAR_PRICE, priceSize.toFixed(2) + 'px');

    var target = priceRef * priceSize / REF;
    var min = parseFloat(cs.getPropertyValue('--ffg-bar-name-min')) || 11;
    var max = parseFloat(cs.getPropertyValue('--ffg-bar-name-max')) || priceSize;
    var size = Math.max(min, Math.min(max, REF * target / nameRef));

    /* THE FLOOR YIELDS TO THE TRACK. When the solve lands below the 11px
       floor (a plural name over a shrunken price in a tight column), the
       floor holds the name a fraction WIDER than the price — and if that
       exceeds the track, CSS ellipsises it. "LARGE BA…" at 11px is strictly
       worse than "LARGE BARS" at 10.4px, so fitting beats the floor.
       Found 30 Jul: track 69.18px, floored name 69.3px, ellipsis by 0.12px. */
    if (isFinite(room) && room > 0 && nameRef * size / REF > room) {
      /* 0.97, not exact (13 Aug, Devin's iPad: "the name of the size
         get's cut off" on 18 LARGE + 60 SMALL) — the exact solve aims the
         name at precisely the track's width, and real-device font
         rounding (headless fit, iPad ellipsised) only needs one pixel of
         overshoot to trip the ellipsis. Three percent buys the rounding
         back. */
      size = REF * room / nameRef * 0.97;
    }

    ROOT.style.setProperty(VAR_SIZE, size.toFixed(2) + 'px');
  }

  function unfit() {
    ROOT.style.removeProperty(VAR_SIZE);
    ROOT.style.removeProperty(VAR_PRICE);
  }

  function readQuantity() {
    var input = document.querySelector(QTY);
    var n = input ? parseInt(input.value, 10) : 1;
    /* A number input can legitimately be empty mid-typing. Treat anything that
       is not a positive integer as 1, which falls through to the no-override
       path rather than rendering $0.00. */
    return isFinite(n) && n > 0 ? n : 1;
  }

  /* The unit price AS SHOPIFY FORMATTED IT. Taken from the selected chip's
     ::after, which the generated stylesheet fills with `{{ v.price | money }}`.
     `.closest()` rather than `:has()` so this does not depend on selector
     support — see the Safari notes in the field notes §7. */
  function formatSample() {
    var checked = document.querySelector('.product-details .variant-option__button-label input:checked');
    var chip = checked && checked.closest('.variant-option__button-label');
    return chip ? pseudoText(getComputedStyle(chip, '::after')) : null;
  }

  /* WHY THE SAMPLE EXISTS instead of Intl.NumberFormat or a hardcoded "$".
     A shop's money_format is a merchant setting: it can carry a suffix
     ("$18.00 USD"), a comma decimal, a space or apostrophe group separator, or
     no decimals at all. Shopify has already rendered one correct example of it
     on the selected chip, so the punctuation is derived from that rather than
     guessed. If the sample is unreadable we fall back to plain "$1,234.56",
     which is right for this shop today and wrong quietly rather than loudly. */
  function formatMoney(cents, sample) {
    var sign = cents < 0 ? '-' : '';
    cents = Math.abs(cents);

    var prefix = '$', suffix = '', dec = '.', group = ',', showCents = true;
    var m = sample && sample.match(/^(\D*?)([\d][\d.,\s ']*)(\D*)$/);
    if (m) {
      prefix = m[1];
      suffix = m[3];
      var body = m[2];
      var tail = body.match(/[.,](\d{2})$/);
      showCents = !!tail;
      if (tail) dec = body.charAt(body.length - 3);
      var head = tail ? body.slice(0, -3) : body;
      var g = head.match(/[^\d]/);
      if (g) group = g[0];
    }

    var whole = Math.floor(cents / 100);
    var frac = cents % 100;
    var out = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
    if (showCents) out += dec + (frac < 10 ? '0' + frac : frac);
    return sign + prefix + out + suffix;
  }

  function clear() {
    /* Attribute first. Removing the property first would leave one frame where
       the rule still applies with an empty var and the price blanks out. */
    ROOT.removeAttribute(ATTR);
    ROOT.style.removeProperty(VAR);
    ROOT.style.removeProperty(VAR_NAME);
  }

  function sync() {
    var bar = document.querySelector(BAR);
    if (!bar) { clear(); unfit(); return; }
    syncTotal(bar);
    /* After, not before: the name is measured against the price that is
       actually showing, which above quantity 1 is the line total. */
    fitName(bar);
  }

  function syncTotal(bar) {
    var cents = readCents(bar);
    if (cents === null) return clear();      // multi-option product, or the block's guard is off

    var qty = readQuantity();
    if (qty <= 1) return clear();            // the generated unit-price rule is already correct

    /* JSON.stringify gives a CSS-safe quoted string and escapes any quote the
       money format or a product name might contain. */
    ROOT.style.setProperty(VAR, JSON.stringify(formatMoney(cents * qty, formatSample())));

    /* The name is optional. If the block hasn't emitted --ffg-unit-name the
       property is simply not set, the ::before override has nothing to show,
       and the singular generated rule keeps the label — same fallback shape as
       the price. */
    var name = readProp(bar, '--ffg-unit-name');
    if (name) {
      var plural = readProp(bar, '--ffg-unit-name-plural') || pluralise(name);
      ROOT.style.setProperty(VAR_NAME, JSON.stringify(plural));
    } else {
      ROOT.style.removeProperty(VAR_NAME);
    }

    ROOT.setAttribute(ATTR, '');
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () { frame = 0; sync(); });
  }

  /* Delegated on the document and capturing, so they survive every re-render
     of the form. `click` is the safety net for the stepper keys: our capture
     handler runs before Horizon's, but the rAF lands after it, so the input
     has its new value by the time sync() reads it. */
  document.addEventListener('input', schedule, true);
  document.addEventListener('change', schedule, true);
  document.addEventListener('click', schedule, true);
  window.addEventListener('pageshow', schedule);

  /* Both of these change the answer without touching the DOM: a resize can
     cross the 359px breakpoint and change the price's size, and a webfont
     landing changes every width on the page. Without the fonts hook the name
     is sized against the fallback face and settles a few percent wrong. */
  window.addEventListener('resize', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

  /* A variant tap replaces the form wholesale and fires no event we can rely
     on afterwards, so the swap itself is the signal. Scoped to the section
     wrapper, not the body, and it only ever calls sync() — this observer never
     writes into the tree it watches, so it cannot loop. */
  function observe() {
    var scope = document.querySelector('[id^="ProductInformation-"]');
    var host = (scope && scope.closest('[id^="shopify-section-"]')) || document.body;
    new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(); sync(); });
  } else {
    observe();
    sync();
  }
})();
